/**
 * Y reply quick-choices.
 *
 * Y posts are procedurally generated, so we can't author per-NPC reply trees
 * the way we did for phone/email. Instead, the choices shown to the player
 * depend on the DETECTED TOPIC of the post (mentions $TICKER, mentions a
 * politician, is a doom take, is a "i'm broke" wsb post, etc), and the NPC
 * reply pool is keyed by (tribe × topic) so the same handle replies in voice.
 *
 * Each YReplyTopicTree:
 *   - matches a post via regex or fn
 *   - lists 3-6 quick-reply choices the player can fire
 *   - has per-tribe response pools (and a fallback pool)
 */

import type { TribeId } from '@/content/xcloneHandles';

export interface YReplyChoice {
  id: string;
  label: string;
  playerSays: string;          // appears as the player's reply post body
  // Optional: bias the NPC response valence ('agree' / 'disagree' / 'neutral')
  // so the response pool can pick fitting lines.
  valence?: 'agree' | 'disagree' | 'shitpost' | 'concern';
}

export interface YReplyTopicTree {
  id: string;
  match: (post: { text: string; tribe?: TribeId }) => boolean;
  choices: YReplyChoice[];
  // Per-tribe per-valence response pools for the NPC reply.
  responses: Partial<Record<TribeId, Partial<Record<NonNullable<YReplyChoice['valence']>, string[]>>>>;
  // If tribe not in `responses`, fall back to this generic pool.
  fallback: Partial<Record<NonNullable<YReplyChoice['valence']>, string[]>>;
}

// ─── Topic 1: ticker mention ($TARI / $BOOM / etc) ───
const TICKER_TOPIC: YReplyTopicTree = {
  id: 'ticker',
  match: (p) => /\$[A-Z]{3,5}/.test(p.text),
  choices: [
    { id: 'agree-bull', label: 'agree (bullish)', playerSays: "agreed. long.", valence: 'agree' },
    { id: 'disagree-bear', label: 'disagree (bearish)', playerSays: "you\'re wrong. short it.", valence: 'disagree' },
    { id: 'this-you', label: '"this you?" with screenshot', playerSays: "this you? [screenshot]", valence: 'shitpost' },
    { id: 'ratio', label: 'ratio them', playerSays: "L take. ratio incoming.", valence: 'shitpost' },
    { id: 'concern', label: 'concern troll', playerSays: "are you ok? this seems unwell.", valence: 'concern' },
  ],
  responses: {
    wsb: {
      agree: ["BROTHER. WAGMI.", "u get it. follow back.", "FINALLY. someone with a brain in this thread."],
      disagree: ["cope. seethe. paper hands.", "ngmi. blocked.", "ratio + L + skill issue + cope"],
      shitpost: ["bro this is unhinged but i\'m laughing", "respect. genuinely.", "screenshotted for the discord"],
      concern: ["bro i appreciate you. i\'m not ok. but i appreciate you."],
    },
    fintwit: {
      agree: ["fair. mostly.", "i\'ll grant you this one.", "rare W from a stranger. respect."],
      disagree: ["this is a top-decile bad take. blocked.", "imagine typing this with confidence.", "screenshot for the bad-takes thread."],
      shitpost: ["genuinely funny. follow back.", "i can\'t believe you said this in public."],
      concern: ["i appreciate the energy but this is a public market not a wellness check."],
    },
    cnbc: {
      agree: ["Appreciate the engagement. Will note.", "Thank you for the considered reply."],
      disagree: ["We will not be engaging with this tone.", "Noted. The Network does not respond to ratios."],
      shitpost: ["This will not be addressed on-air."],
      concern: ["The Network appreciates your concern. The talent is fine. Allegedly."],
    },
    crypto: {
      agree: ["WAGMI 🚀", "u are SO early on this view ser", "based ser. follow back."],
      disagree: ["have fun staying poor", "ngmi. cope. not financial advice.", "weak hands detected. blocked."],
      shitpost: ["lmao based 💀"],
      concern: ["ser i\'m fine. the candle is fine. everything is fine."],
    },
    maga: {
      agree: ["BASED. PATRIOTS RISE.", "finally a real american take.", "screencapping this for truth social."],
      disagree: ["LIBERAL TEARS DETECTED 😂", "cope harder lib.", "blocked. america wins."],
      shitpost: ["lmao destroy them king."],
      concern: ["save the concern for the deep state, friend."],
    },
    left: {
      agree: ["finally. someone reasonable.", "rare to see this kind of take here.", "sharing this. thank you."],
      disagree: ["screenshotted for the group chat where we discuss accounts like yours.", "what an exhausting take.", "blocked. enjoy your hellscape."],
      shitpost: ["okay this one is funny. don\'t make it a habit."],
      concern: ["that\'s actually thoughtful. go take a break."],
    },
    press: {
      agree: ["Thank you for engaging on the record.", "Will reach out for full quote."],
      disagree: ["Hostile reply logged. We may include in coverage.", "Counsel will handle further engagement."],
      shitpost: ["Filing this under 'tweets we will publish without comment.'"],
      concern: ["Are you on the record about this concern? I will publish it either way."],
    },
    mom: {
      agree: ["that\'s sweet sweetie. love mom."],
      disagree: ["sweetie. that\'s not how we talk to people. love mom."],
      shitpost: ["i don\'t know what this means but i\'ll show your father. love mom."],
      concern: ["did you eat today? love mom."],
    },
  },
  fallback: {
    agree: ["thanks for the boost", "appreciated"],
    disagree: ["fair enough", "noted, blocked"],
    shitpost: ["lmao", "🫠"],
    concern: ["i\'m fine, thanks"],
  },
};

// ─── Topic 2: politician mention (Trump / Biden / Powell / etc) ───
const POLITICIAN_TOPIC: YReplyTopicTree = {
  id: 'politician',
  match: (p) => /\b(Trump|Biden|Powell|JPow|Vance|Kamala|MTG|AOC|RFK|DeSantis|Newsom|Sanders|the President|PRESIDENT)\b/i.test(p.text),
  choices: [
    { id: 'agree-pol', label: 'agree with the politics', playerSays: "100% this. nailed it.", valence: 'agree' },
    { id: 'disagree-pol', label: 'disagree with the politics', playerSays: "absolutely not. terrible take.", valence: 'disagree' },
    { id: 'mock', label: 'mock everyone in this thread', playerSays: "you all sound exactly the same. log off.", valence: 'shitpost' },
    { id: 'apolitical', label: '"i\'m here for the markets, not this"', playerSays: "i\'m here for the markets. take this elsewhere.", valence: 'concern' },
  ],
  responses: {
    maga: {
      agree: ["FINALLY a patriot in this thread 🇺🇸🦅", "based brother. follow.", "the real America is HERE."],
      disagree: ["LIBERAL TRIGGERED 😂", "cope. seethe. cry harder.", "another snowflake spotted."],
      shitpost: ["nah we\'re cooking. you\'re the one who should log off."],
      concern: ["everything is political now, friend. wake up."],
    },
    left: {
      agree: ["a normal person on this hellsite??", "thank you for making sense.", "saving this."],
      disagree: ["of course you think that.", "screenshotted.", "blocked. life is short."],
      shitpost: ["this one is fair. carry on."],
      concern: ["if only we had the luxury. but everything is political. by design."],
    },
    fintwit: {
      agree: ["fair point on the politician. the trade thesis is the part that matters though.", "agreed but mostly because it\'s priced in.", "not engaging on the politics. agree on the trade impact."],
      disagree: ["the politics is wrong. the trade is also wrong. impressive symmetry.", "no. wrong. blocked."],
      shitpost: ["finally someone with sense in this politics-poisoned thread."],
      concern: ["respect. unusual to see in this thread."],
    },
    press: {
      agree: ["Thank you for the engagement. May we cite?", "Noted on the record."],
      disagree: ["Hostility logged. May appear in coverage."],
      shitpost: ["Filing under 'tweets we will reference but not engage with.'"],
      concern: ["This account also has politics. The 'no politics' framing is itself a politics. Source: a journalist."],
    },
    cnbc: {
      agree: ["Acknowledged."],
      disagree: ["Acknowledged. The Network does not engage."],
      shitpost: ["Acknowledged."],
      concern: ["Acknowledged. The Network is, definitionally, here for the markets."],
    },
    wsb: {
      agree: ["politics is just another stock. respect.", "agree but only because both sides keep printing money for me.", "based. now back to the casino."],
      disagree: ["bro touch grass.", "log off and yolo something.", "couldn\'t care less. show portfolio."],
      shitpost: ["this thread is a -EV trade. blocked. lmao."],
      concern: ["respect for trying. it won\'t work. but respect."],
    },
    crypto: {
      agree: ["fiat politics is the old game. we\'re building the new one.", "based ser. crypto solves this.", "WAGMI regardless of who wins."],
      disagree: ["statism in any flavor is the disease, my friend.", "ngmi if you think any politician fixes this.", "decentralize or die."],
      shitpost: ["all politicians are the same shitcoin. different ticker."],
      concern: ["ser the entire stack is political. you can\'t opt out."],
    },
    mom: {
      agree: ["politics?? sweetie don\'t get into politics on the internet. love mom."],
      disagree: ["sweetie don\'t argue with people about this. nothing good comes of it. love mom."],
      shitpost: ["i don\'t know what this is sweetie. love mom."],
      concern: ["good sweetie. stay out of it. love mom."],
    },
  },
  fallback: {
    agree: ["agreed", "fair"],
    disagree: ["nah", "wrong"],
    shitpost: ["okay this is funny"],
    concern: ["respect"],
  },
};

// ─── Topic 3: loss / "i'm broke" / wsb-style despair ───
const LOSS_TOPIC: YReplyTopicTree = {
  id: 'loss',
  match: (p) => /\b(blew up|blown up|lost|broke|underwater|liquidat(ed|ion)|guh|down bad|wiped|rekt|GUH)\b/i.test(p.text),
  choices: [
    { id: 'condolences', label: 'send condolences', playerSays: "F. genuinely. hang in there.", valence: 'agree' },
    { id: 'roast', label: 'roast them', playerSays: "skill issue. this is what happens when you don\'t use stops.", valence: 'shitpost' },
    { id: 'venmo', label: 'offer to venmo $20', playerSays: "venmo me your handle. i got you for $20.", valence: 'agree' },
    { id: 'concern-real', label: 'check on them genuinely', playerSays: "are you actually okay though? please reply.", valence: 'concern' },
  ],
  responses: {
    wsb: {
      agree: ["bro. F. F. F.", "thanks brother. WAGMI eventually.", "appreciate it bro. seriously."],
      disagree: ["nah you\'re right. cope received.", "stops are for cowards. but you\'re right."],
      shitpost: ["bro this is exactly what i needed. thank you for not coddling me.", "the roast was earned. respect.", "lmao thank you actually."],
      concern: ["i\'m. not. but thank you for asking. for real."],
    },
    fintwit: {
      agree: ["appreciated. a rare kindness on this site."],
      disagree: ["fair criticism. accepted."],
      shitpost: ["the deserved roast. taken."],
      concern: ["thank you. genuinely. i\'ll be okay. unclear when."],
    },
    crypto: {
      agree: ["WAGMI eventually ser. it always comes back. allegedly."],
      disagree: ["fair ser. ngmi.", "yeah. probably ngmi."],
      shitpost: ["fair. ngmi. respect though."],
      concern: ["ser. i\'m not. but the chain is forever. thank you."],
    },
    mom: {
      agree: ["thank you sweetie. it gets better. love mom."],
      disagree: ["sweetie that\'s harsh. but. thank you. love mom."],
      shitpost: ["sweetie don\'t kick someone when they\'re down. love mom."],
      concern: ["i love you sweetie. please call me. love mom."],
    },
    cnbc: {
      agree: ["Acknowledged. The Network appreciates the engagement."],
      disagree: ["Acknowledged."],
      shitpost: ["Acknowledged. Off-color, but acknowledged."],
      concern: ["Acknowledged. The talent is, allegedly, fine."],
    },
    maga: {
      agree: ["respect to a fellow patriot in tough times.", "back on the saddle, friend. America wins.", "we lift each other up here. unlike the libs."],
      disagree: ["bro you\'re losing because the system is rigged. wake up.", "stops are for people who don\'t believe."],
      shitpost: ["lmao based. self-aware kings only."],
      concern: ["go pray on it. genuinely. and then YOLO again."],
    },
    left: {
      agree: ["this whole system is designed to do this to people. solidarity.", "it\'s not your fault. it\'s the structure."],
      disagree: ["the take is fine. the underlying system is the problem. but the take is fine."],
      shitpost: ["this thread is 90% people who should not be in markets. you fit right in."],
      concern: ["genuinely take care of yourself. log off. read a book."],
    },
    press: {
      agree: ["Going on the record about your loss? It\'s a great human-interest piece."],
      disagree: ["Hostility on a public loss tweet — interesting."],
      shitpost: ["Quotable. Filing it."],
      concern: ["Are you okay enough to be on the record about not being okay?"],
    },
  },
  fallback: {
    agree: ["thank you", "appreciated"],
    disagree: ["yeah you\'re right"],
    shitpost: ["fair enough"],
    concern: ["i\'ll be okay"],
  },
};

// ─── Topic 4: news / breaking ───
const NEWS_TOPIC: YReplyTopicTree = {
  id: 'news',
  match: (p) => /\b(BREAKING|breaking|reports|sources|just in|developing)\b/.test(p.text),
  choices: [
    { id: 'fake', label: '"fake news"', playerSays: "fake news.", valence: 'disagree' },
    { id: 'source', label: 'demand a source', playerSays: "source?", valence: 'concern' },
    { id: 'amplify', label: 'amplify it', playerSays: "this is huge. spreading.", valence: 'agree' },
    { id: 'jaded', label: '"and yet nothing will happen"', playerSays: "and yet nothing will happen. as usual.", valence: 'shitpost' },
  ],
  responses: {
    press: {
      disagree: ["The reporting is sourced. Three independent confirmations. Filing with editor.", "Calling 'fake news' is, journalistically, a 'comment.' We will run it."],
      concern: ["Source is a person familiar with the matter. We will not name them. You will not need to know."],
      agree: ["Thank you for amplifying. We will note your engagement."],
      shitpost: ["Cynicism is, journalistically, an off-the-record source. Logged."],
    },
    maga: {
      disagree: ["EXACTLY. fake news media strikes again.", "BASED. they LIE."],
      agree: ["spreading the truth, patriot.", "yes. WAKE UP."],
      shitpost: ["THIS. they never face consequences. by design."],
      concern: ["the source is, of course, the deep state. now you know."],
    },
    left: {
      disagree: ["calling reporting 'fake news' is the move of someone afraid of the reporting."],
      agree: ["finally getting attention on this. share it everywhere."],
      shitpost: ["yes. this is by design. you understand."],
      concern: ["the source is a person doing their job. which is more than the people calling it fake can say."],
    },
    cnbc: {
      disagree: ["The Network stands by its reporting."],
      concern: ["Source: senior official. We do not name sources. You will see it in print."],
      agree: ["Thank you. Stay tuned."],
      shitpost: ["The Network is, technically, watching."],
    },
    wsb: {
      disagree: ["yeah probably nothing. blocking the thread."],
      agree: ["yolo\'d into it. WAGMI.", "loading up. WAGMI."],
      shitpost: ["lmao yeah it never matters. tape will be green tomorrow regardless."],
      concern: ["bro the source is some dude on a discord. don\'t worry about it."],
    },
    crypto: {
      disagree: ["fiat news. zero relevance to the chain.", "tradfi nonsense. WAGMI regardless."],
      agree: ["spreading on telegram. ser this is huge.", "WAGMI energy."],
      shitpost: ["lol every breaking news on this site is somebody\'s exit liquidity."],
      concern: ["the source is a wallet ser. the chain doesn\'t lie."],
    },
    fintwit: {
      disagree: ["the reporting is, technically, sound. \"fake\" is wishful."],
      agree: ["agreed. but priced in within minutes. don\'t chase."],
      shitpost: ["accurate. the half-life of \"breaking\" is 6 minutes."],
      concern: ["sourced. you can disagree with the framing but the facts check out."],
    },
    mom: {
      disagree: ["sweetie don\'t fight people about news on the internet. love mom."],
      agree: ["i\'ll show your father. love mom."],
      shitpost: ["okay sweetie. love mom."],
      concern: ["sweetie i don\'t know what to believe anymore. love mom."],
    },
  },
  fallback: {
    disagree: ["whatever"],
    concern: ["the source is, allegedly, real"],
    agree: ["thanks for the boost"],
    shitpost: ["yeah. agreed."],
  },
};

// ─── Topic 5: AI / LLM / agent posts ───
const AI_TOPIC: YReplyTopicTree = {
  id: 'ai',
  match: (p) => /\b(AI|LLM|GPT|chatGPT|openai|anthropic|agent|model|alignment|AGI|prompt|hallucinat)/i.test(p.text),
  choices: [
    { id: 'ai-bull', label: 'AGI is real and imminent', playerSays: "AGI is genuinely close. people aren\'t pricing this in.", valence: 'agree' },
    { id: 'ai-bear', label: 'AI bubble incoming', playerSays: "this is the dotcom bubble in a tighter time-frame. brace.", valence: 'disagree' },
    { id: 'ai-shrug', label: '"yeah but who pays for it"', playerSays: "yeah but who actually pays for any of this. real question.", valence: 'concern' },
    { id: 'ai-mock', label: '"it\'s just a fancy autocomplete"', playerSays: "it\'s a fancy autocomplete. respectfully.", valence: 'shitpost' },
  ],
  responses: {
    fintwit: {
      agree: ["respect the conviction. i think you\'re early. early enough that you\'re wrong.", "agree. but the trade is, somehow, not a trade. that\'s the AI bubble in a sentence."],
      disagree: ["this is the right call. the timing of the right call is, however, not a thing the right call gives you.", "fair. but the inevitability of being right does not make you immediately right."],
      shitpost: ["fancy autocomplete that just won an IMO problem. so. measure of fancy is, perhaps, generous to autocomplete."],
      concern: ["the answer to 'who pays' is, increasingly, 'someone else, eventually.' the bubble is about the gap."],
    },
    crypto: {
      agree: ["AGI + on-chain identity is the killer combo. WAGMI.", "tokenize the model. tokenize the inference. tokenize the prompt. WAGMI."],
      disagree: ["fair. but. AGI is bullish for crypto either way. WAGMI."],
      shitpost: ["lmao based. autocomplete with extra steps. our chain is also extra steps."],
      concern: ["the chain pays. we always pay. WAGMI."],
    },
    cnbc: {
      agree: ["The Network is, definitionally, exposed to AI. We acknowledge this."],
      disagree: ["Bubble talk has been priced in 14 times. We will price it in 14 more."],
      shitpost: ["Acknowledged."],
      concern: ["A fair question. Also a question we are required, by sponsor read, to ask carefully."],
    },
    wsb: {
      agree: ["bro this is the trade. all in. AGI by EOY.", "respect. sized up. WAGMI."],
      disagree: ["fair but i\'m yolo\'ing OpenAI calls regardless."],
      shitpost: ["lmao based. fancy autocomplete trillion dollar market cap."],
      concern: ["nobody pays bro. that\'s the trade."],
    },
    press: {
      agree: ["Going on record about AGI is, journalistically, very quotable. We will run it."],
      disagree: ["Bubble framing is, journalistically, also very quotable. Filing both."],
      shitpost: ["The 'fancy autocomplete' quote will be in approximately 14 articles by Friday."],
      concern: ["The 'who pays' question is, in fact, our pitch for the AI Sunday cover. Thank you."],
    },
    maga: {
      agree: ["America AGI > China AGI. simple. patriotic. correct.", "AGI is, structurally, a national-security asset. handle it accordingly."],
      disagree: ["AI is a deep-state psyop to replace real American jobs. WAKE UP."],
      shitpost: ["lmao the elites trust this thing more than they trust voters. that tells you something."],
      concern: ["the real question is who PROFITS. the answer is the same people who always profit."],
    },
    left: {
      agree: ["the AGI labs are, structurally, large social-engineering experiments funded by people who are, by their own admission, terrified of what they\'re building."],
      disagree: ["agreed. the bubble is in the financialization. the technology is real. the money story is fake."],
      shitpost: ["the 'fancy autocomplete' framing is funny. it is also, strategically, what the labs want you to say. so."],
      concern: ["nobody pays directly. the public will pay indirectly. that\'s the model."],
    },
    mom: {
      agree: ["sweetie i don\'t know what this means but you sound smart. love mom."],
      disagree: ["sweetie don\'t fight people about robots online. love mom."],
      shitpost: ["i used the chat bot at the bank. it didn\'t help. is that what you\'re talking about. love mom."],
      concern: ["good question sweetie. love mom."],
    },
  },
  fallback: {
    agree: ["agreed. early but agreed."],
    disagree: ["bubble incoming. eventually."],
    shitpost: ["it\'s autocomplete with PR."],
    concern: ["nobody pays. that\'s the secret."],
  },
};

// ─── Topic 6: generic catch-all (always matches as last resort) ───
const GENERIC_TOPIC: YReplyTopicTree = {
  id: 'generic',
  match: () => true,
  choices: [
    { id: 'agree-gen', label: 'agree', playerSays: "agreed.", valence: 'agree' },
    { id: 'disagree-gen', label: 'disagree', playerSays: "no.", valence: 'disagree' },
    { id: 'lmao', label: 'lmao', playerSays: "lmao", valence: 'shitpost' },
    { id: 'snark', label: 'snark', playerSays: "okay champ.", valence: 'shitpost' },
  ],
  responses: {
    wsb: {
      agree: ["respect", "based", "follow back"],
      disagree: ["cope", "L"],
      shitpost: ["lmao based", "respect bro"],
    },
    fintwit: {
      agree: ["noted"],
      disagree: ["fair"],
      shitpost: ["fine. you got me."],
    },
    cnbc: {
      agree: ["Acknowledged."],
      disagree: ["Acknowledged."],
      shitpost: ["Acknowledged."],
    },
    crypto: {
      agree: ["WAGMI"],
      disagree: ["ngmi"],
      shitpost: ["lmao based ser"],
    },
    maga: {
      agree: ["based"],
      disagree: ["LIBERAL"],
      shitpost: ["destroy them king"],
    },
    left: {
      agree: ["thank you"],
      disagree: ["of course you think that"],
      shitpost: ["okay this one is funny"],
    },
    press: {
      agree: ["Noted."],
      disagree: ["Logged."],
      shitpost: ["Acknowledged."],
    },
    mom: {
      agree: ["love mom"],
      disagree: ["sweetie."],
      shitpost: ["i don\'t know what this means. love mom."],
    },
  },
  fallback: {
    agree: ["thanks"],
    disagree: ["whatever"],
    shitpost: ["lol"],
  },
};

const TOPIC_TREES: YReplyTopicTree[] = [
  TICKER_TOPIC,
  POLITICIAN_TOPIC,
  LOSS_TOPIC,
  NEWS_TOPIC,
  AI_TOPIC,
  GENERIC_TOPIC,  // catch-all — always matches; keep last
];

export function pickYReplyTree(post: { text: string; tribe?: TribeId }): YReplyTopicTree {
  for (const t of TOPIC_TREES) {
    if (t.match(post)) return t;
  }
  return GENERIC_TOPIC;
}

export function pickYReplyResponse(
  tree: YReplyTopicTree,
  tribe: TribeId | undefined,
  valence: NonNullable<YReplyChoice['valence']>,
): string | null {
  const tribePool = tribe ? tree.responses[tribe] : undefined;
  const pool = tribePool?.[valence] ?? tree.fallback[valence];
  if (!pool || pool.length === 0) return null;
  return pool[Math.floor(Math.random() * pool.length)] ?? null;
}
